import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, Search, Smartphone, Type, Mail, Check, 
  Loader, ArrowRight, Globe, Sparkles 
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import SEO from '../components/SEO';
import emailjs from '@emailjs/browser';

function FreeTools() {
  const { language } = useLanguage();
  const { showToast } = useToast();
  const [activeTool, setActiveTool] = useState(null);

  // Website Speed Test Tool
  const SpeedTestTool = () => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [email, setEmail] = useState('');
    const [showEmailForm, setShowEmailForm] = useState(false);

    const runSpeedTest = async () => {
      if (!url) return;
      setLoading(true);
      
      // Simulate speed test (in real app, use Google PageSpeed API)
      setTimeout(() => {
        const score = Math.floor(Math.random() * 40) + 50; // 50-90
        const loadTime = (Math.random() * 3 + 1).toFixed(2); // 1-4s
        
        setResult({
          score,
          loadTime,
          rating: score >= 80 ? 'Good' : score >= 60 ? 'Needs Improvement' : 'Poor',
          suggestions: [
            'Optimize images (reduce file size by 60%)',
            'Enable browser caching',
            'Minify CSS and JavaScript',
            'Use a Content Delivery Network (CDN)',
            'Reduce server response time'
          ]
        });
        setLoading(false);
        setShowEmailForm(true);
      }, 2000);
    };

    const sendReport = async (e) => {
      e.preventDefault();
      try {
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TOOLS_TEMPLATE_ID,
          {
            tool_name: 'Website Speed Test',
            user_email: email,
            website_url: url,
            speed_score: result.score,
            load_time: result.loadTime,
            rating: result.rating
          },
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );
        showToast(
          language === 'sq' ? 'Raporti u dërgua në email!' : 'Report sent to email!',
          'success'
        );
      } catch (error) {
        showToast(
          language === 'sq' ? 'Gabim në dërgim' : 'Error sending',
          'error'
        );
      }
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {language === 'sq' ? 'URL e Website-it' : 'Website URL'}
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
            />
            <button
              onClick={runSpeedTest}
              disabled={!url || loading}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              {loading ? 'Testing...' : 'Test'}
            </button>
          </div>
        </div>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className={`text-4xl font-bold mb-1 ${
                  result.score >= 80 ? 'text-green-500' : 
                  result.score >= 60 ? 'text-yellow-500' : 'text-red-500'
                }`}>
                  {result.score}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sq' ? 'Score' : 'Score'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-1 text-blue-500">{result.loadTime}s</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sq' ? 'Kohë Ngarkimi' : 'Load Time'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-1 text-purple-500">{result.rating}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sq' ? 'Vlerësim' : 'Rating'}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                {language === 'sq' ? 'Rekomandime për Përmirësim:' : 'Improvement Recommendations:'}
              </h4>
              <ul className="space-y-2">
                {result.suggestions.map((suggestion, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>

            {showEmailForm && (
              <form onSubmit={sendReport} className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {language === 'sq' ? 'Merr raportin e plotë në email:' : 'Get full report via email:'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
                  />
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    {language === 'sq' ? 'Dërgo' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </div>
    );
  };

  // SEO Checker Tool
  const SEOCheckerTool = () => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const checkSEO = () => {
      if (!url) return;
      setLoading(true);
      
      setTimeout(() => {
        setResult({
          score: Math.floor(Math.random() * 30) + 60,
          issues: [
            { type: 'error', message: 'Missing meta description' },
            { type: 'warning', message: 'Title tag is too short' },
            { type: 'success', message: 'Mobile-friendly design detected' },
            { type: 'warning', message: 'Images missing alt text' },
            { type: 'success', message: 'HTTPS enabled' }
          ]
        });
        setLoading(false);
      }, 1500);
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {language === 'sq' ? 'URL e Website-it' : 'Website URL'}
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
            />
            <button
              onClick={checkSEO}
              disabled={!url || loading}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 transition-all flex items-center gap-2"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {loading ? 'Checking...' : 'Check'}
            </button>
          </div>
        </div>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-yellow-500 mb-2">{result.score}/100</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">SEO Score</div>
            </div>

            <div className="space-y-2">
              {result.issues.map((issue, idx) => (
                <div 
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    issue.type === 'error' ? 'bg-red-50 dark:bg-red-900/20' :
                    issue.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                    'bg-green-50 dark:bg-green-900/20'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    issue.type === 'error' ? 'bg-red-500' :
                    issue.type === 'warning' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`} />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{issue.message}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    );
  };

  // Mobile Responsiveness Checker
  const MobileCheckerTool = () => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const checkMobile = () => {
      if (!url) return;
      setLoading(true);
      
      setTimeout(() => {
        setResult({
          isMobileFriendly: Math.random() > 0.3,
          score: Math.floor(Math.random() * 30) + 70,
          details: {
            viewport: Math.random() > 0.5,
            textSize: Math.random() > 0.4,
            tapTargets: Math.random() > 0.6,
            contentWidth: Math.random() > 0.5
          }
        });
        setLoading(false);
      }, 1500);
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {language === 'sq' ? 'URL e Website-it' : 'Website URL'}
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
            />
            <button
              onClick={checkMobile}
              disabled={!url || loading}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 transition-all flex items-center gap-2"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
              {loading ? 'Checking...' : 'Check'}
            </button>
          </div>
        </div>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className={`text-center mb-6 p-6 rounded-xl ${
              result.isMobileFriendly 
                ? 'bg-green-100 dark:bg-green-900/30' 
                : 'bg-red-100 dark:bg-red-900/30'
            }`}>
              <div className={`text-4xl font-bold mb-2 ${
                result.isMobileFriendly ? 'text-green-600' : 'text-red-600'
              }`}>
                {result.isMobileFriendly ? '✓' : '✗'}
              </div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {result.isMobileFriendly 
                  ? (language === 'sq' ? 'Mobile-Friendly' : 'Mobile-Friendly')
                  : (language === 'sq' ? 'Jo Mobile-Friendly' : 'Not Mobile-Friendly')}
              </div>
              <div className="text-3xl font-bold text-gray-700 dark:text-gray-300 mt-2">
                {result.score}/100
              </div>
            </div>

            <div className="space-y-3">
              <CheckItem 
                label={language === 'sq' ? 'Viewport Configuration' : 'Viewport Configuration'} 
                passed={result.details.viewport} 
              />
              <CheckItem 
                label={language === 'sq' ? 'Text Readable' : 'Text Readable'} 
                passed={result.details.textSize} 
              />
              <CheckItem 
                label={language === 'sq' ? 'Tap Targets Sized' : 'Tap Targets Sized'} 
                passed={result.details.tapTargets} 
              />
              <CheckItem 
                label={language === 'sq' ? 'Content Width Correct' : 'Content Width Correct'} 
                passed={result.details.contentWidth} 
              />
            </div>
          </motion.div>
        )}
      </div>
    );
  };

  const CheckItem = ({ label, passed }) => (
    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
        passed ? 'bg-green-500' : 'bg-red-500'
      }`}>
        {passed ? <Check className="w-4 h-4 text-white" /> : '✗'}
      </div>
    </div>
  );

  // Domain Name Generator
  const DomainGeneratorTool = () => {
    const [keyword, setKeyword] = useState('');
    const [suggestions, setSuggestions] = useState([]);

    const generateDomains = () => {
      if (!keyword) return;
      
      const prefixes = ['get', 'try', 'my', 'the', 'go', 'pro', 'best'];
      const suffixes = ['app', 'hub', 'lab', 'studio', 'zone', 'pro', 'online'];
      const domains = [];

      // Direct domains
      domains.push(`${keyword}.com`, `${keyword}.net`, `${keyword}.io`);
      
      // With prefixes
      prefixes.forEach(prefix => {
        domains.push(`${prefix}${keyword}.com`);
      });
      
      // With suffixes
      suffixes.forEach(suffix => {
        domains.push(`${keyword}${suffix}.com`);
      });

      setSuggestions(domains.slice(0, 12));
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {language === 'sq' ? 'Keyword për Domain' : 'Domain Keyword'}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value.toLowerCase().replace(/\s+/g, ''))}
              placeholder="mybusiness"
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
            />
            <button
              onClick={generateDomains}
              disabled={!keyword}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 transition-all flex items-center gap-2"
            >
              <Globe className="w-5 h-5" />
              {language === 'sq' ? 'Gjenero' : 'Generate'}
            </button>
          </div>
        </div>

        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid sm:grid-cols-2 gap-3"
          >
            {suggestions.map((domain, idx) => (
              <div
                key={idx}
                className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-yellow-500 dark:hover:border-yellow-500 transition-all cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-gray-900 dark:text-white">{domain}</span>
                  <Sparkles className="w-4 h-4 text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    );
  };

  const tools = [
    {
      id: 'speed',
      icon: Zap,
      title: { sq: 'Website Speed Test', en: 'Website Speed Test' },
      description: { sq: 'Testo shpejtësinë e website-it tënd dhe merr rekomandime', en: 'Test your website speed and get recommendations' },
      component: SpeedTestTool
    },
    {
      id: 'seo',
      icon: Search,
      title: { sq: 'SEO Checker', en: 'SEO Checker' },
      description: { sq: 'Analizo SEO-në e website-it tënd', en: 'Analyze your website SEO' },
      component: SEOCheckerTool
    },
    {
      id: 'mobile',
      icon: Smartphone,
      title: { sq: 'Mobile Responsiveness', en: 'Mobile Responsiveness' },
      description: { sq: 'Kontrollo nëse website-i është mobile-friendly', en: 'Check if website is mobile-friendly' },
      component: MobileCheckerTool
    },
    {
      id: 'domain',
      icon: Globe,
      title: { sq: 'Domain Name Generator', en: 'Domain Name Generator' },
      description: { sq: 'Gjenero sugjerime për emra domain', en: 'Generate domain name suggestions' },
      component: DomainGeneratorTool
    }
  ];

  return (
    <>
      <SEO 
        title={language === 'sq' ? 'Free Tools - Mjete Falas' : 'Free Tools'}
        description={language === 'sq' ? 'Mjete falas për të analizuar website-in tuaj' : 'Free tools to analyze your website'}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 sm:py-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              {language === 'sq' ? 'Mjete Falas' : 'Free Tools'}
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {language === 'sq' ? 'Mjete të Fuqishme për Website-in Tuaj' : 'Powerful Tools for Your Website'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {language === 'sq' 
                ? 'Analizo, testo, dhe përmirëso website-in tënd me mjetet tona falas' 
                : 'Analyze, test, and improve your website with our free tools'}
            </p>
          </motion.div>

          {/* Tools Grid */}
          {!activeTool ? (
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {tools.map((tool, index) => (
                <motion.button
                  key={tool.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setActiveTool(tool)}
                  className="p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl border border-gray-200 dark:border-gray-700 hover:border-yellow-500 dark:hover:border-yellow-500 transition-all text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <tool.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {tool.title[language]}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {tool.description[language]}
                      </p>
                      <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 font-semibold">
                        {language === 'sq' ? 'Përdore →' : 'Use Tool →'}
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto"
            >
              <button
                onClick={() => setActiveTool(null)}
                className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-semibold transition-colors"
              >
                ← {language === 'sq' ? 'Kthehu' : 'Back to Tools'}
              </button>

              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-10 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
                    <activeTool.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {activeTool.title[language]}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {activeTool.description[language]}
                    </p>
                  </div>
                </div>

                <activeTool.component />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
}

export default FreeTools;